import type { PropsWithChildren, ReactNode } from 'react'

const proseClass =
  'article-prose max-w-none text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty] ' +
  '[&_h1]:mt-10 [&_h1]:mb-4 [&_h1]:font-display [&_h1]:text-[24px] [&_h1]:font-normal [&_h1]:leading-[27px] [&_h1]:tracking-[-0.24px] [&_h1]:text-[var(--foreground)] ' +
  '[&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-display [&_h2]:text-[20px] [&_h2]:font-normal [&_h2]:leading-6 [&_h2]:tracking-[-0.2px] [&_h2]:text-[var(--foreground)] ' +
  '[&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-display [&_h3]:text-[17px] [&_h3]:font-normal [&_h3]:leading-[21px] [&_h3]:tracking-[-0.17px] [&_h3]:text-[var(--foreground)] ' +
  '[&_p]:mb-4 [&_p]:text-[var(--foreground)] ' +
  '[&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-[var(--border-strong)] [&_a]:transition-colors hover:[&_a]:decoration-[var(--foreground)] ' +
  '[&_a]:focus-visible:outline-none [&_a]:focus-visible:ring-2 [&_a]:focus-visible:ring-[var(--ring)] [&_a]:focus-visible:ring-offset-2 ' +
  '[&_strong]:font-semibold [&_em]:italic ' +
  '[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:space-y-3 [&_ol]:pl-5 ' +
  '[&_li]:pl-1 [&_li]:marker:text-[var(--foreground-muted)] ' +
  '[&_blockquote]:my-8 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--border-strong)] [&_blockquote]:pl-4 [&_blockquote]:text-[var(--foreground-muted)] ' +
  '[&_hr]:my-10 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-[var(--border)] ' +
  '[&_table]:my-8 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[14px] [&_table]:leading-[19.25px] ' +
  '[&_th]:border [&_th]:border-[var(--border)] [&_th]:bg-[#fafafa] [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-semibold [&_th]:uppercase [&_th]:leading-[16.5px] [&_th]:tracking-[0.275px] [&_th]:text-[var(--foreground-muted)] ' +
  '[&_td]:border [&_td]:border-[var(--border)] [&_td]:px-3 [&_td]:py-2.5 ' +
  '[&_code]:rounded-[4px] [&_code]:bg-[var(--surface-quiet)] [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em] ' +
  '[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-[var(--radius-md)] [&_pre]:border [&_pre]:border-[var(--border)] [&_pre]:bg-[var(--surface-quiet)] [&_pre]:p-4 [&_pre]:font-mono [&_pre]:text-sm ' +
  '[&_img]:my-6 [&_img]:max-h-[420px] [&_img]:w-auto [&_img]:max-w-full [&_img]:rounded-[var(--radius-md)] [&_img]:border [&_img]:border-[var(--border)]'

export const ArticleProse = ({ children }: PropsWithChildren) => (
  <div className={proseClass}>{children}</div>
)

export const renderRichText = (text: string) => {
  const parts: ReactNode[] = []
  const tokenPattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = tokenPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const token = match[0]
    const key = `${token}-${match.index}`
    const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/)

    if (link) {
      parts.push(
        <a key={key} href={link[2]} target="_blank" rel="noreferrer">
          {link[1]}
        </a>,
      )
    } else if (token.startsWith('**')) {
      parts.push(<strong key={key}>{token.slice(2, -2)}</strong>)
    } else if (token.startsWith('*')) {
      parts.push(<em key={key}>{token.slice(1, -1)}</em>)
    } else {
      parts.push(<code key={key}>{token.slice(1, -1)}</code>)
    }

    lastIndex = tokenPattern.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts
}

type ParagraphsProps = {
  paragraphs?: string[]
}

export const ProseParagraphs = ({ paragraphs }: ParagraphsProps) => {
  if (!paragraphs?.length) {
    return null
  }
  return (
    <ArticleProse>
      {paragraphs.map((text, i) => (
        <p key={i}>{renderRichText(text)}</p>
      ))}
    </ArticleProse>
  )
}
